import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, X, ImageUp, Loader2 } from "lucide-react";

/** Shared decode logic for BOTH camera scan and image upload (spec §6 — one
 * validation path, one business-connection process either way). Accepts our
 * own `konexo://connect?code=XXX` URIs and also a bare code. */
function extractConnectCode(decoded) {
  const uriMatch = decoded.match(/[?&]code=([A-Z0-9]{4,16})/i);
  if (uriMatch) return uriMatch[1].toUpperCase();
  if (/^[A-Z0-9]{4,16}$/i.test(decoded.trim())) return decoded.trim().toUpperCase();
  return "";
}

/**
 * #3 native camera QR scanner used from the "Add business" flow.
 * Wraps html5-qrcode. Emits `onDecoded(code)` with the parsed supplier code.
 * Phase 4 — adds an "Upload QR Image" tab that decodes a photo/gallery image
 * via the same `extractConnectCode` + `onDecoded` path as the camera.
 */
export function QRScanner({ open, onClose, onDecoded }) {
  const containerId = "konexo-qr-reader";
  const fileReaderId = "konexo-qr-reader-file";
  const [mode, setMode] = useState("camera"); // "camera" | "upload"
  const [starting, setStarting] = useState(true);
  const [err, setErr] = useState("");
  const [uploadErr, setUploadErr] = useState("");
  const [decoding, setDecoding] = useState(false);
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopCamera = () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      s.stop().catch(() => {}).finally(() => {
        try { s.clear(); } catch (_e) { /* "scan ongoing" race on fast unmount — harmless */ }
      });
    }
  };

  useEffect(() => {
    if (!open || mode !== "camera") return;
    let cancelled = false;

    (async () => {
      setStarting(true);
      setErr("");
      try {
        const scanner = new Html5Qrcode(containerId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decoded) => {
            if (cancelled) return;
            const code = extractConnectCode(decoded);
            if (!code) {
              setErr("Not a KonexoCRM QR — point at your partner's business code.");
              return;
            }
            // Guard: only stop/clear once, even if the unmount cleanup also
            // fires. Leaving these to run in parallel can leave the <video>
            // element live and show a blank/frozen frame to the user.
            stopCamera();
            onDecoded(code);
          },
          () => {
            /* onErr per-frame — ignore; too noisy */
          }
        );
        if (!cancelled) setStarting(false);
      } catch (ex) {
        if (cancelled) return;
        setStarting(false);
        setErr(
          ex && ex.message && ex.message.includes("Permission")
            ? "Camera permission denied — please allow camera access and retry."
            : "Could not start the camera. Try again or enter the code manually."
        );
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [open, mode, onDecoded]);

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadErr("");
    setDecoding(true);
    try {
      const reader = new Html5Qrcode(fileReaderId);
      let decodedText = "";
      try {
        decodedText = await reader.scanFile(file, false);
      } finally {
        try { reader.clear(); } catch (_e) { /* noop */ }
      }
      const code = extractConnectCode(decodedText || "");
      if (!code) {
        setUploadErr("No valid KonexoCRM QR code found. Please try another image.");
        return;
      }
      onDecoded(code);
    } catch (_ex) {
      // #7 — no QR found / unreadable image — never crash, always show a clear message.
      setUploadErr("No valid KonexoCRM QR code found. Please try another image.");
    } finally {
      setDecoding(false);
    }
  };

  const switchMode = (next) => {
    if (next === mode) return;
    if (mode === "camera") stopCamera();
    setErr("");
    setUploadErr("");
    setMode(next);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 grid place-items-center p-4" data-testid="qr-scanner-overlay">
      <div className="bg-white rounded-3xl overflow-hidden max-w-md w-full shadow-2xl">
        <div className="px-4 pt-4 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white grid place-items-center">
              <Camera className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-slate-900 text-sm">Scan business QR</div>
              <div className="text-[11px] text-slate-500">Camera or upload a QR image</div>
            </div>
          </div>
          <button
            onClick={() => { stopCamera(); onClose(); }}
            data-testid="qr-scanner-close"
            className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 grid place-items-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Section 5 — two methods, same underlying validation */}
        <div className="px-4 pb-3 flex bg-slate-100 rounded-2xl p-1 mx-4" data-testid="qr-scanner-mode-tabs">
          <button
            onClick={() => switchMode("camera")}
            data-testid="qr-scanner-mode-camera"
            className={`flex-1 h-9 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
              mode === "camera" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            <Camera className="w-3.5 h-3.5" /> Scan with Camera
          </button>
          <button
            onClick={() => switchMode("upload")}
            data-testid="qr-scanner-mode-upload"
            className={`flex-1 h-9 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 ${
              mode === "upload" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            <ImageUp className="w-3.5 h-3.5" /> Upload QR Image
          </button>
        </div>

        {mode === "camera" ? (
          <>
            <div className="relative bg-black" data-testid="qr-scanner-container">
              <div id={containerId} className="w-full aspect-square" />
              {starting && (
                <div className="absolute inset-0 grid place-items-center text-white text-xs">Starting camera...</div>
              )}
            </div>
            {err && (
              <div className="px-4 py-3 text-xs text-red-700 bg-red-50 border-t border-red-100" data-testid="qr-scanner-error">
                {err}
                {err.includes("permission") && (
                  <button onClick={() => window.location.reload()} className="ml-2 underline font-semibold">Retry</button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="px-4 pb-5">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFileChosen}
              data-testid="qr-upload-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={decoding}
              data-testid="qr-upload-choose-btn"
              className="w-full aspect-square rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 hover:bg-slate-100 grid place-items-center gap-2 text-slate-500 transition disabled:opacity-60"
            >
              {decoding ? (
                <>
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <span className="text-xs font-semibold">Reading QR code...</span>
                </>
              ) : (
                <>
                  <ImageUp className="w-8 h-8" strokeWidth={1.8} />
                  <span className="text-sm font-semibold">Tap to choose an image</span>
                  <span className="text-[11px] text-slate-400">Gallery, photos, or files</span>
                </>
              )}
            </button>
            {/* Hidden — dedicated element for file-based decoding, never renders a live camera. */}
            <div id={fileReaderId} className="hidden" />
            {uploadErr && (
              <div className="mt-3 px-3 py-2.5 rounded-xl text-xs text-red-700 bg-red-50 border border-red-100" data-testid="qr-upload-error">
                {uploadErr}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
